import { Stack } from '@mui/joy';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BillToLocationFormField } from 'contacts/elements';
import { ContactGroupType } from 'contacts/enums';
import { Button } from 'core/components';
import { NON_EMPTY, NOT_AVAILABLE } from 'core/constants';
import { useFormContext } from 'core/states';
import { GetManyResponse, IAutocompleteOption } from 'core/types';
import { CorporateLocationFormField, ShipmentTypeFormField } from 'corporate/elements';
import { CorporateLocationType } from 'corporate/enums';
import { ICorporateLocationAutocomplete } from 'corporate/types';
import { IExternalPricesCustomization } from 'parts/types';
import { ProjectFormField } from 'projects/components/ProjectFormField';
import { Direction } from 'theme/enums';

interface IProps {
  partNumber: string;
  manufacturerId: number;
  onApply: (values: IExternalPricesCustomization) => Promise<{ success: boolean }>;
  isApplying?: boolean;
}

const fieldSx = { flex: '1 1 140px', maxWidth: 180 };
const wideListboxStyle = { minWidth: 320 };

export const CustomizationRowFormFields = ({
  partNumber,
  manufacturerId,
  onApply,
  isApplying,
}: IProps) => {
  const { t } = useTranslation();

  const { watch, handleSubmit, dirtyFields, reset } = useFormContext();

  const isDirty = Object.keys(dirtyFields).length > 0;
  const customerBillToValue = watch('customerBillTo');
  const projectValue = watch('project');

  const onSubmit = async (values: IExternalPricesCustomization) => {
    const { success } = await onApply(values);

    if (!success) {
      return;
    }

    reset(values);
  };

  const customerBillToRequestPayload = useMemo(() => {
    const baseRequestPayload = {
      filters: {
        contactGroupType: ContactGroupType.CUSTOMER,
      },
    };

    const projectId = projectValue?.id;

    if (!projectId) {
      return baseRequestPayload;
    }

    return {
      ...baseRequestPayload,
      filters: { ...baseRequestPayload.filters, projectId },
    };
  }, [projectValue]);

  const projectRequestPayload = useMemo(() => {
    const billToAccountNumber = customerBillToValue?.accountsReceivableId;

    if (!billToAccountNumber) {
      return {};
    }

    return {
      filters: {
        accountNumbers: {
          billTo: billToAccountNumber,
        },
      },
    };
  }, [customerBillToValue]);

  return (
    <Stack direction={Direction.ROW} sx={{ gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <Stack sx={fieldSx}>
        <CorporateLocationFormField
          name="shippingLocation"
          label=""
          placeholder={t('labelShippingLocation')}
          type={CorporateLocationType.SHIPPING}
          requestPayload={{
            filters: { vendorId: NON_EMPTY },
            viewBy: { partNumber, manufacturerId },
          }}
          queryOptions={{
            mapResult: (result: unknown): unknown => ({
              records: (result as GetManyResponse<ICorporateLocationAutocomplete>).records.map(
                (record) => ({
                  ...record,
                  addressName: record.name,
                  vendorIdentifier: record.vendorId,
                  inventoryTotalQty: record.inventoryQuantity,
                })
              ),
            }),
          }}
          getLabel={(option: IAutocompleteOption) =>
            [
              option.addressName || NOT_AVAILABLE,
              option.externalLocationId ? `(${option.externalLocationId})` : '',
            ]
              .filter(Boolean)
              .join(' ')
          }
          displayInventory
          isAlternateVersionEnabled
          required={false}
          disableClearable={false}
          listboxStyle={wideListboxStyle}
        />
      </Stack>

      <Stack sx={fieldSx}>
        <CorporateLocationFormField
          name="salesLocation"
          label=""
          placeholder={t('labelSalesLocation')}
          type={CorporateLocationType.SALES}
          enabledOnFocus={false}
          isAlternateVersionEnabled
          required={false}
          disableClearable={false}
          listboxStyle={wideListboxStyle}
        />
      </Stack>

      <Stack sx={fieldSx}>
        <ShipmentTypeFormField
          name="shipmentType"
          label=""
          placeholder={t('labelShipmentType')}
          required={false}
          disableClearable={false}
        />
      </Stack>

      <Stack sx={fieldSx}>
        <BillToLocationFormField
          name="customerBillTo"
          label=""
          placeholder={t('labelProspect').toUpperCase()}
          requestPayload={customerBillToRequestPayload}
          required={false}
          disableClearable={false}
          listboxStyle={wideListboxStyle}
          hasCreateOption={false}
        />
      </Stack>

      <Stack sx={fieldSx}>
        <ProjectFormField
          defaultDtdProject={false}
          hasCreateOption={false}
          required={false}
          disableClearable={false}
          label=""
          placeholder={t('labelProject')}
          requestPayload={projectRequestPayload}
          listboxStyle={wideListboxStyle}
        />
      </Stack>

      <Button
        text={t('labelApply')}
        loading={isApplying}
        disabled={!isDirty}
        onClick={handleSubmit(onSubmit)}
      />
    </Stack>
  );
};
